import { Component, OnInit, Input } from '@angular/core';
import { filter, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { classifyDocumentFiles, coerceDocumentFilesArray } from '@flexus/utilities';

export interface SilFileSummaryShape {
	claim: number;
	created: string;
	description: unknown;
	filename: string;
	id: number;
	job: number;
	on_maven: boolean;
	purpose: string;
	thumbnail: string; //b64
	token: string;
}

/** Config for one document tab when using tenant-driven dynamic tabs (e.g. from required_documents). */
export interface DocumentTabConfig {
	targetId: string;
	display: string;
	purpose: string;
	displayMode?: 'image-grid' | 'file-grid';
}

@Component({
	selector: 'flxap-view-documents',
	templateUrl: './view-documents.component.html',
	styleUrls: ['./view-documents.component.scss']
})
export class FLXAPViewDocumentsComponent implements OnInit {
	@Input() location: string;
	@Input() files$: Observable<SilFileSummaryShape[]>;
	@Input() quotations$: Observable<SilFileSummaryShape[]>;
	@Input() photos$: Observable<SilFileSummaryShape[]>;
	@Input() reports$: Observable<SilFileSummaryShape[]>;
	@Input() latestDocuments$: Observable<SilFileSummaryShape[]>;
	@Input() invoices$: Observable<SilFileSummaryShape[]>;
	geyserPhotos$: Observable<SilFileSummaryShape[]>;
	buildingPhotos$: Observable<SilFileSummaryShape[]>;

	@Input() selectDocsToView: string[];
	/** When set, tabs are built from this config and files$ is filtered by purpose per tab (dynamic mode). */
	@Input() documentTabsConfig: DocumentTabConfig[];
	/** When true, show Supporting Documents (catch-all) plus explicit sections derived from files$. */
	@Input() useDynamicTab = false;

	tabFiles$: Record<string, Observable<SilFileSummaryShape[]>> = {};
	supportingDocuments$: Observable<SilFileSummaryShape[]> = of([]);
	useDynamicTabs = false;

	constructor() {}

	ngOnInit() {
		if (this.useDynamicTab && this.files$) {
			this.applyClassifiedDocumentSections(this.files$);
		} else if (this.documentTabsConfig?.length && this.files$) {
			this.useDynamicTabs = true;
			this.documentTabsConfig.forEach(tab => {
				const purposeNorm = this.normalizePurpose(tab.purpose);
				this.tabFiles$[tab.targetId] = this.files$.pipe(
					filter((arr): arr is SilFileSummaryShape[] => Array.isArray(arr)),
					map(arr => arr.filter(f => this.normalizePurpose(f.purpose) === purposeNorm))
				);
			});
		} else if (this.selectDocsToView) {
			const inputArray = this.selectDocsToView;
			const observableArr = ['photos', 'reports', 'quotations', 'invoices', 'latestDocuments'];
			for (let i = 0; i <= inputArray.length - 1; i++) {
				for (let j = 0; j <= observableArr.length - 1; j++)
					if (inputArray[i] === observableArr[j]) {
						const index = observableArr.indexOf(observableArr[j]);
						observableArr.splice(index, 1);
					}
			}
			if (observableArr.includes('photos')) {
				this.photos$ = null;
			}
			if (observableArr.includes('invoices')) {
				this.invoices$ = null;
			}
			if (observableArr.includes('quotations')) {
				this.quotations$ = null;
			}
			if (observableArr.includes('reports')) {
				this.reports$ = null;
			}
			if (observableArr.includes('latestDocuments')) {
				this.latestDocuments$ = null;
			}
		} else if (this.photos$) {
			this.geyserPhotos$ = this.photos$.pipe(map((files: SilFileSummaryShape[]) => files.filter(entry => entry.purpose?.toLowerCase() === 'geyser')));
			this.buildingPhotos$ = this.photos$.pipe(map((files: SilFileSummaryShape[]) => files.filter(entry => entry.purpose?.toLowerCase() === 'building')));
		}
	}

	private applyClassifiedDocumentSections(files$: Observable<SilFileSummaryShape[] | { files?: SilFileSummaryShape[]; data?: SilFileSummaryShape[] }>): void {
		const classified$ = files$.pipe(
			map(arrOrObj => coerceDocumentFilesArray(arrOrObj)),
			filter((arr): arr is SilFileSummaryShape[] => Array.isArray(arr)),
			map(files => classifyDocumentFiles<SilFileSummaryShape>(files, { purposeBasedPhotos: true }))
		);

		this.supportingDocuments$ = classified$.pipe(map(c => c.supportingDocuments));
		this.photos$ = classified$.pipe(map(c => c.photos));
		this.quotations$ = classified$.pipe(map(c => c.quotations));
		this.invoices$ = classified$.pipe(map(c => c.invoices));
		this.reports$ = classified$.pipe(map(c => c.reports));
		this.latestDocuments$ = classified$.pipe(map(c => c.latestDocuments));
	}

	normalizePurpose(p: string): string {
		return (p ?? '').trim().toLowerCase().replace(/\s+/g, '_');
	}

	getFilesForTab(tab: DocumentTabConfig): Observable<SilFileSummaryShape[]> {
		return this.tabFiles$[tab.targetId] ?? of([]);
	}

	getDisplayMode(tab: DocumentTabConfig): 'image-grid' | 'file-grid' {
		return tab.displayMode ?? (this.normalizePurpose(tab.purpose) === 'photo' ? 'image-grid' : 'file-grid');
	}
}
